/**
 * Visual indicator for WebSocket connection status.
 * Shows connected/connecting/disconnected state with optional reconnect action.
 */

import type { WebSocketConnectionStatus } from '@saaga/shared/hooks/use-agents-websocket.hook'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2Icon, WifiIcon, WifiOffIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const connectionStatusVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
  {
    variants: {
      status: {
        connected: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
        connecting: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
        disconnected: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      },
    },
    defaultVariants: {
      status: 'disconnected',
    },
  }
)

export interface ChatConnectionStatusProps extends VariantProps<typeof connectionStatusVariants> {
  status: WebSocketConnectionStatus
  onReconnect?: () => void
  className?: string
}

const StatusIcon: React.FC<{ status: WebSocketConnectionStatus }> = ({ status }) => {
  const iconClassName = 'size-3.5'

  const iconMap: Record<WebSocketConnectionStatus, ReactNode> = {
    connected: <WifiIcon className={iconClassName} />,
    connecting: <Loader2Icon className={cn(iconClassName, 'animate-spin')} />,
    disconnected: <WifiOffIcon className={iconClassName} />,
  }

  return iconMap[status]
}

const statusLabels: Record<WebSocketConnectionStatus, string> = {
  connected: 'Connected',
  connecting: 'Connecting...',
  disconnected: 'Disconnected',
}

const ChatConnectionStatusBase: React.FC<ChatConnectionStatusProps> = ({
  status,
  onReconnect,
  className,
}) => (
  <div className={cn('chat-connection-status flex items-center gap-2', className)}>
    <span className={connectionStatusVariants({ status })}>
      <StatusIcon status={status} />
      {statusLabels[status]}
    </span>
    {status === 'disconnected' && onReconnect && (
      <Button
        variant="ghost"
        size="sm"
        onClick={onReconnect}
        className="chat-reconnect-button h-auto px-2 py-1 text-xs"
      >
        Reconnect
      </Button>
    )}
  </div>
)

export const ChatConnectionStatus = memo(ChatConnectionStatusBase)

ChatConnectionStatus.displayName = 'ChatConnectionStatus'
